// src/features/commodities-ranking/components/CategoryRadar.tsx
//
// Commodity-category factor radar. Each spoke is a commodity category
// (Energy / Metals / Agriculture / Livestock); the three overlaid series
// are the category-average Momentum / Commercials (COT) / Speculators
// (COT) z-scores returned by GET /api/v1/commodities-ranking/category-
// summary. This lets a user see, at a glance, which factor is "lit up" in
// which corner of the commodity complex (e.g. Energy strong on Momentum,
// Metals heavily net-long by speculators). Sibling of
// features/etf-ranking/components/CategoryRadar.

import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import type { CategorySummaryRow } from '../api';

interface CategoryRadarProps {
    categories: CategorySummaryRow[];
}

// Recharts wants one object per spoke with a key per series.
interface RadarDatum {
    category: string;
    Momentum: number;
    Commercials: number;
    Speculators: number;
}

// Category buckets are stored as plain tokens (Energy / Metals / ...);
// de-underscore any compound tokens for legibility on the angle axis.
const prettify = (category: string): string => category.replace(/_/g, ' ');

const RadarTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: <span className="font-mono">{p.value.toFixed(2)}</span>
                </p>
            ))}
        </div>
    );
};

export const CategoryRadar = ({ categories }: CategoryRadarProps) => {
    if (!categories || categories.length === 0) {
        return (
            <p className="muted text-sm">
                No category aggregates available yet.
            </p>
        );
    }

    const data: RadarDatum[] = categories.map((c) => ({
        category: prettify(c.category),
        Momentum: Number(c.avg_momentum_z.toFixed(3)),
        Commercials: Number(c.avg_fundamental_z.toFixed(3)),
        Speculators: Number(c.avg_sentiment_z.toFixed(3)),
    }));

    return (
        <div
            className="w-full h-96 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Average factor z-scores by commodity category"
        >
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data} outerRadius="72%">
                    <PolarGrid className="stroke-slate-200 dark:stroke-slate-700" />
                    <PolarAngleAxis
                        dataKey="category"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        tick={{ fontSize: 10, fill: 'currentColor' }}
                    />
                    <Radar
                        name="Momentum"
                        dataKey="Momentum"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Commercials"
                        dataKey="Commercials"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Speculators"
                        dataKey="Speculators"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.15}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip content={<RadarTooltip />} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CategoryRadar;
